import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetCheques,
  CreateCheque,
  UpdateCheque,
  DeleteCheque,
  UpdateChequeStatus,
} from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import {
  DEFAULT_STALE_TIME,
  LOOKUP_STALE_TIME,
} from "../Constant/AppConstants";

export const chequeQueryKeys = {
  cheques: (params?: Parameters<typeof GetCheques>[0]) =>
    ["cheques", params] as const,
};

export const invalidateChequeQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["cheques"] });
};

export const useChequesQuery = (
  params: Parameters<typeof GetCheques>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: chequeQueryKeys.cheques(params),
    queryFn: async () => {
      const res = await GetCheques(params);
      if (!res?.success) {
        return { data: [], total: 0 };
      }
      return {
        data: res.data || [],
        total: res.meta?.total || 0,
      };
    },
  });
};

export const useChequesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetCheques>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: chequeQueryKeys.cheques(params),
        queryFn: async () => {
          const res = await GetCheques(params);
          if (!res?.success) {
            return { data: [], total: 0 };
          }
          return {
            data: res.data || [],
            total: res.meta?.total || 0,
          };
        },
      }),
  });
};

export const useCreateChequeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateCheque>[0]) =>
      CreateCheque(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateChequeQueries(queryClient);
      }
    },
  });
};

export const useUpdateChequeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateCheque>[0]) =>
      UpdateCheque(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateChequeQueries(queryClient);
      }
    },
  });
};

export const useDeleteChequeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeleteCheque>[0]) =>
      DeleteCheque(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateChequeQueries(queryClient);
      }
    },
  });
};

export const useUpdateChequeStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateChequeStatus>[0]) =>
      UpdateChequeStatus(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateChequeQueries(queryClient);
      }
    },
  });
};

export const useChequeService = (config?: {
  fetchChequesParams?: Parameters<typeof GetCheques>[0];
  fetchChequesEnabled?: boolean;
}) => {
  const chequesQuery = useChequesQuery(config?.fetchChequesParams, {
    enabled:
      config?.fetchChequesEnabled !== false &&
      config?.fetchChequesParams !== undefined &&
      config?.fetchChequesParams !== null,
  });

  const createChequeMutation = useCreateChequeMutation();
  const updateChequeMutation = useUpdateChequeMutation();
  const deleteChequeMutation = useDeleteChequeMutation();
  const updateChequeStatusMutation = useUpdateChequeStatusMutation();

  return {
    chequesQuery,
    createChequeMutation,
    updateChequeMutation,
    deleteChequeMutation,
    updateChequeStatusMutation,
  };
};
